import { useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import {
	type ReverifyTotals,
	useReverify,
} from "@/features/plausibility/use-reverify";

/**
 * Re-verify the alerts the old keyword table down-ranked from Stage 1's words
 * alone, before every alert was checked against its footage. Each goes back to
 * its original severity and wording, then through the verifier like any other
 * alert. No notifications are sent.
 */
export const ReverifyPanel = () => {
	const [progress, setProgress] = useState<null | ReverifyTotals>(null);
	const reverify = useReverify(setProgress);
	const totals = reverify.isPending ? progress : (reverify.data ?? progress);
	const previewed = reverify.data?.dryRun && reverify.data.remaining > 0;

	return (
		<section className="rounded-md border border-secondary p-4">
			<h3 className="text-sm font-semibold text-primary">
				Re-verify text-only down-ranks
			</h3>
			<p className="text-sm text-secondary">
				Alerts that were lowered from Stage 1's words alone, without the footage
				being reviewed. Each is put back to its original severity and wording,
				then checked against its footage. Customers are not notified.
			</p>
			<div className="mt-3 flex flex-wrap items-center gap-2">
				<Button
					color="secondary"
					isDisabled={reverify.isPending}
					onClick={() => {
						setProgress(null);
						reverify.mutate({ dryRun: true });
					}}
					size="sm"
					type="button"
				>
					Preview (dry run)
				</Button>
				<Button
					isDisabled={reverify.isPending || !previewed}
					onClick={() => {
						if (
							window.confirm(
								`Re-verify ${reverify.data?.remaining ?? 0} alerts? Their severity and wording go back to what Stage 1 said until the footage check lands.`,
							)
						) {
							setProgress(null);
							reverify.mutate({ dryRun: false });
						}
					}}
					size="sm"
					type="button"
				>
					{reverify.isPending && totals && !totals.dryRun
						? "Re-verifying…"
						: "Re-verify"}
				</Button>
			</div>
			{totals && (
				<p className="mt-2 text-sm text-secondary" role="status">
					{totals.dryRun
						? `${totals.remaining} alerts were down-ranked without a footage review.`
						: `Queued ${totals.enqueued} for a footage check${
								totals.failed > 0 ? `, ${totals.failed} failed` : ""
							}; ${totals.remaining} remaining.`}
				</p>
			)}
			{reverify.error && (
				<p className="mt-2 text-sm text-error-primary" role="alert">
					{reverify.error.message}
				</p>
			)}
		</section>
	);
};
